import _ from "lodash";
import classNames from "classnames";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "Hooks/useDispatch";
import { useMutation, useQuery } from "@apollo/client";

import { Button, Checkbox, IconButton } from "@toddle-edu/ds-web";
import {
  ClipboardPencilOutlined,
  CloseOutlined,
  PlayCircleOutlined,
  QuickTaskOutlined,
  RefreshArrowOutlined,
  StarFilled,
} from "@toddle-edu/ds-icons";

import { useI18n } from "Hooks";
import ACLStore from "lib/aclStore";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { createAssessmentTool } from "modules/AssessmentTool/AssessmentToolModule";
import {
  createOpeningMessage,
  resetAITutorConversation,
} from "AppComponents/AITutorChatWrapper/modules/AITutorChatModule";
import {
  getAITutorConversation,
  getAITutorConversationFromCache,
} from "AppComponents/AITutorChatWrapper/modules/AITutorChatGraphqlHelpers";
import { createIbPypElementRatingsWithoutValue } from "ClassRoom/modules/ClassRoomModule";
import AssessmentIcon from "UIComponents/AssessmentIcon";
import Loading from "UIComponents/Loading";
import FullScreenLoader from "UIComponents/FullScreenLoader/FullScreenLoader";

import {
  CATEGORY_OPTIONS,
  FEATURE_FLAG_KEY,
  I18N_NAMESPACE,
  SPECIAL_OPTIONS,
} from "../modules/Constants";
import { getAITutorAwardsReviewQuery } from "../modules/AITutorAwardsQueries";
import { updateAITutorAwardEntriesMutation } from "../modules/AITutorAwardsMutations";
import {
  REVIEWER_ELIGIBLE_ORG_ID,
  RUBRIC_SOURCE_ASSESSMENT_ID,
} from "./rubric";
import classes from "./AITutorAwardsReviewer.scss";
import { getEnvironment } from "Utils/UrlHelpers";

// Use require() wrapped in a getter to hide these deps from madge — they
// transitively reach back through RichTextEditor → LinkifyModal → Post →
// Worksheet → AssessmentDetails → Header → AITutorAwardsReviewer and close
// new cycles vs develop. Same pattern as
// AppComponents/CopyUnitPlanModal/LibraryFeed/LibraryFeed.js.
const getRichTextEditor = () => require("UIComponents/RichTextEditor").default;
const getToolResponse = () =>
  require("AssessmentEvaluation/components/ToolResponse").default;
const getAITutorChatWrapper = () =>
  require("AppComponents/AITutorChatWrapper").default;

const styles = {
  toolResponseContainerStyle: { marginBottom: 0 },
  evaluationStatusExtraParams: { teacherEvaluationText: "Evaluation" },
};

const RTE_PLUGINS_CONFIG = {
  image: { isEnabled: true },
  video: { isEnabled: false },
  audio: { isEnabled: false },
  file: { isEnabled: true },
  table: { isEnabled: false },
  link: { isEnabled: false },
  mathsEditor: { isEnabled: false },
  ai: { isEnabled: false },
  toolbar: {
    textColor: { isEnabled: false },
    headings: { isEnabled: false },
    uploadButton: { isEnabled: true },
    showFullScreenButton: false,
  },
};

const getCategoryLabelKey = value => {
  const match = _.find(CATEGORY_OPTIONS, { value });
  return _.get(match, "labelKey", null);
};

const getSpecialityLabelKey = value => {
  const match = _.find(SPECIAL_OPTIONS, { value });
  return _.get(match, "labelKey", null);
};

const Section = ({ label, headerRight, children }) => (
  <div className={classes.section}>
    {headerRight ? (
      <div className={classes.rubricSectionHeader}>
        <span className={classNames(classes.sectionLabel, "text-label-xs")}>
          {label}
        </span>
        {headerRight}
      </div>
    ) : (
      <span className={classNames(classes.sectionLabel, "text-label-xs")}>
        {label}
      </span>
    )}
    <div className={classes.sectionBody}>{children}</div>
  </div>
);

const BonusPointRow = ({ checked, onChange, disabled }) => {
  const handleToggle = () => {
    if (!disabled) onChange(!checked);
  };
  return (
    <div className={classes.bonusRow}>
      <div className={classes.bonusLabelCell}>
        <div>
          <div className={classNames(classes.bonusBadge, "text-body")}>
            <StarFilled size="xx-small" className={classes.bonusStarIcon} />
            BONUS
          </div>
          <div className={classNames(classes.bonusSubLabel, "text-body-s")}>
            Exceptional overall impression
          </div>
        </div>
        <div className={classNames(classes.bonusPoints, "text-body")}>
          +1 pt
        </div>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={classNames(classes.bonusToggleCell, {
          [classes.bonusToggleCellChecked]: checked,
        })}
        aria-label="bonus"
      >
        <Checkbox
          dsVersion="2.0"
          isChecked={checked}
          onChange={handleToggle}
          size="medium"
          disabled={disabled}
          testId="aiTutorAwards-reviewer-bonus-checkbox"
        />
        <div className={classes.bonusToggleBody}>
          <span className={classNames(classes.bonusToggleTitle, "text-body")}>
            Award bonus point
          </span>
          <span className={classNames(classes.bonusToggleHint, "text-body-s")}>
            This tutor left a lasting impression. It stands out across all three
            criteria in a way that goes beyond any individual score. You&apos;d
            recommend it to other educators without hesitation.
          </span>
        </div>
      </button>
    </div>
  );
};

// Outer wrapper: gates eligibility (feature flag + staging/org check) so the
// inner component — which fires queries and mutations on mount — never even
// instantiates for users who shouldn't see the reviewer flow.
const AITutorAwardsReviewer = props => {
  const { org_id: jwtOrgId } = useMemo(() => getUserInfo(), []);
  const isEligibleViewer = useMemo(() => {
    if (!ACLStore.can(FEATURE_FLAG_KEY)) return false;
    const isStagingEnv = _.includes(
      ["staging", "development"],
      getEnvironment()
    );

    if (isStagingEnv) return true;
    return String(jwtOrgId) === REVIEWER_ELIGIBLE_ORG_ID;
  }, [jwtOrgId]);

  if (!isEligibleViewer) return null;
  return <AITutorAwardsReviewerInner {...props} />;
};

const AITutorAwardsReviewerInner = ({ assessmentId, assessmentType }) => {
  const RichTextEditor = getRichTextEditor();
  const ToolResponse = getToolResponse();
  const AITutorChatWrapper = getAITutorChatWrapper();
  const { t } = useI18n([I18N_NAMESPACE, "common"]);
  const dispatch = useDispatch();
  const isAITutor = assessmentType === "ai_tutor";

  const [isOpen, setIsOpen] = useState(false);
  const [isCreatingRubric, setIsCreatingRubric] = useState(false);
  const [activeTab, setActiveTab] = useState("submission");
  const [remarks, setRemarks] = useState("");
  const [isBonusAwarded, setIsBonusAwarded] = useState(false);
  // Bumping `chatSession` re-mounts AITutorChatWrapper after a reset so it
  // re-fetches the (now archived) conversation and regenerates the opening
  // message via its internal init flow.
  const [chatSession, setChatSession] = useState(0);
  const [isResettingChat, setIsResettingChat] = useState(false);
  // Tracks which preview conversations have already been seeded with an
  // opening message during this drawer-open session so we never double-seed.
  const seededOpeningMessageRef = useRef({});
  const drawerContentRef = useRef(null);

  // Drive ToolResponse from local state instead of Apollo's cache: the shared
  // handleAssessmentToolResponseInCache helper has no parentType=ASSESSMENT
  // branch, so cache writes from the cell-click silently no-op.
  const [toolResponsesDataLocal, setToolResponsesDataLocal] = useState({});
  const [ibPYPElementRatingsLocal, setIBPYPElementRatingsLocal] = useState({});

  const { data, refetch } = useQuery(getAITutorAwardsReviewQuery, {
    variables: { id: assessmentId },
    skip: !isAITutor || !assessmentId,
    fetchPolicy: "cache-and-network",
  });

  const entry = _.get(data, "node.aiTutorAwardEntry");
  const rubricTool = useMemo(() => {
    const edges = _.get(data, "node.assessmentTools.edges", []);
    return _.find(
      edges,
      e => _.get(e, "node.assessmentToolType") === "SCORE_BASED_RUBRIC"
    );
  }, [data]);
  const assessmentTool = _.get(rubricTool, "node.assessmentToolItem");
  // node.id is the composite `<itemId>_<TYPE>` (e.g. "378318353787269005_SCORE_BASED_RUBRIC").
  // The mutations want the raw assessmentToolItem id.
  const assessmentToolId = _.get(rubricTool, "node.assessmentToolItem.id");

  // Conversation id of the teacher PREVIEW chat (always created by the editor
  // preview flow). Used to archive messages and regenerate the opening
  // message when the reviewer hits "Reset chat".
  const previewConversationId = useMemo(() => {
    const edges = _.get(data, "node.aiConversation.edges", []);
    return _.get(
      _.find(edges, ({ node }) => _.get(node, "entitySubType") === "PREVIEW"),
      "node.id",
      null
    );
  }, [data]);

  // When the reviewer opens the Play tab on a tutor whose PREVIEW conversation
  // has no messages yet (teacher never previewed it), seed an opening message.
  // The wrapper's own seed path only fires for the editor preview, not for the
  // reviewer's STUDENT_PREVIEW render source.
  useEffect(() => {
    if (!isOpen || activeTab !== "play" || !previewConversationId) return;
    if (seededOpeningMessageRef.current[previewConversationId]) return;

    let cancelled = false;
    (async () => {
      let conversation = getAITutorConversationFromCache({
        id: previewConversationId,
      });
      if (_.isEmpty(conversation)) {
        conversation = await getAITutorConversation({
          id: previewConversationId,
        });
      }
      if (cancelled) return;
      if (!_.isEmpty(_.get(conversation, "messages.edges"))) {
        seededOpeningMessageRef.current[previewConversationId] = true;
        return;
      }
      seededOpeningMessageRef.current[previewConversationId] = true;
      await dispatch(
        createOpeningMessage({
          leId: assessmentId,
          conversationId: previewConversationId,
        })
      );
      if (cancelled) return;
      setChatSession(s => s + 1);
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, activeTab, previewConversationId, assessmentId, dispatch]);

  const categoryKey = entry ? getCategoryLabelKey(entry.category) : null;
  const specialityKey = entry ? getSpecialityLabelKey(entry.speciality) : null;

  // Seed the remarks RTE with the persisted review whenever a fresh entry id
  // arrives.
  useEffect(() => {
    if (!entry) return;
    setRemarks(_.get(entry, "review") || "");
  }, [_.get(entry, "id")]);

  // Seed bonus from any SCORE rating tagged via metadata.isBonus. Stable
  // across rubric re-saves because the rubric thunk doesn't write metadata.
  useEffect(() => {
    const ratings = _.get(data, "node.ibPYPElementRatings", []);
    const bonusRating = _.find(
      ratings,
      r =>
        _.get(r, "ibPYPElementType") === "SCORE" &&
        _.get(r, "metadata.isBonus") === true
    );
    setIsBonusAwarded(!!bonusRating);
  }, [data]);

  // Seed local rubric/rating state from BE on drawer-open so previously-saved
  // selections appear pre-highlighted. handleOpen awaits refetch before
  // flipping isOpen, so this effect runs against fresh data each open.
  useEffect(() => {
    if (!isOpen || !assessmentToolId || !assessmentId || !data) return;

    const persistedToolResponses = _.get(data, "node.toolResponses", []);
    const wrapped = _.isEmpty(persistedToolResponses)
      ? [
          {
            id: `${assessmentToolId}_SCORE_BASED_RUBRIC`,
            toolId: assessmentToolId,
            toolType: "SCORE_BASED_RUBRIC",
            toolResponses: [],
          },
        ]
      : persistedToolResponses;
    setToolResponsesDataLocal(prev => ({ ...prev, [assessmentId]: wrapped }));

    // Strip every bonus-tagged rating (metadata.isBonus is present, true or
    // false) from the array the rubric thunk reads. Bonus uses ibPYPElementType
    // SCORE, and the thunk's override check compares any SCORE rating's value
    // against the recomputed cell sum — leaving a "0"/"1" bonus rating in the
    // way short-circuits the rubric's createIBPYPElementRatings push.
    const persistedRatings = _.filter(
      _.get(data, "node.ibPYPElementRatings", []),
      r => !_.has(r, "metadata.isBonus")
    );

    // If no non-bonus rubric SCORE rating exists yet (cells were saved in a
    // prior session but the SCORE rating wasn't), synthesize one whose value
    // matches the current cell sum. Without this, the thunk's override check
    // sees an empty `previousScoreValue` against a non-zero cell sum, flags
    // the score as overridden, and skips the BE save.
    const hasRubricScoreRating = _.some(
      persistedRatings,
      r => _.get(r, "ibPYPElementType") === "SCORE"
    );
    let seededRatings = persistedRatings;
    if (!hasRubricScoreRating) {
      const cellResponses = _.get(
        _.find(wrapped, e => e.toolId === assessmentToolId),
        "toolResponses",
        []
      );
      const criterias = _.get(
        rubricTool,
        "node.assessmentToolItem.standardRubricCriterias",
        []
      );
      const cellsSum = _.reduce(
        criterias,
        (acc, criteria) => {
          const r = _.find(cellResponses, c => c.rowId === criteria.id);
          if (!r || !r.columnId) return acc;
          const d = _.find(criteria.descriptors, x => x.id === r.columnId);
          return acc + Number(_.get(d, "score", 0) || 0);
        },
        0
      );
      seededRatings = [
        ...persistedRatings,
        {
          id: `synthetic-rubric-score-${assessmentId}`,
          ibPYPElementType: "SCORE",
          value: String(cellsSum),
          creatorType: "STAFF",
          isDeleted: false,
          __typename: "IBPYPElementRating",
        },
      ];
    }
    setIBPYPElementRatingsLocal(prev => ({
      ...prev,
      [assessmentId]: seededRatings,
    }));
  }, [isOpen, data, assessmentId, assessmentToolId, rubricTool]);

  const [updateAwardEntry] = useMutation(updateAITutorAwardEntriesMutation);

  const debouncedSaveRemarks = useMemo(
    () =>
      _.debounce((entryId, value) => {
        updateAwardEntry({
          variables: {
            input: {
              entries: [{ id: entryId, review: value }],
            },
          },
        });
      }, 800),
    [updateAwardEntry]
  );

  useEffect(() => () => debouncedSaveRemarks.cancel(), [debouncedSaveRemarks]);

  const handleRemarksChange = useCallback(
    value => {
      setRemarks(value);
      const entryId = _.get(entry, "id");
      if (!entryId) return;
      debouncedSaveRemarks(entryId, value);
    },
    [entry, debouncedSaveRemarks]
  );

  // Sum of selected descriptor scores per criterion. Recomputed from
  // toolResponsesDataLocal so the chip updates synchronously with each click,
  // independent of the (broken-for-ASSESSMENT) cache write of the SCORE rating.
  const rubricScore = useMemo(() => {
    const responses = _.get(
      _.find(
        _.get(toolResponsesDataLocal, assessmentId, []),
        ({ toolId }) => toolId === assessmentToolId
      ),
      "toolResponses",
      []
    );
    const criterias = _.get(assessmentTool, "standardRubricCriterias", []);
    return _.reduce(
      criterias,
      (acc, criteria) => {
        const response = _.find(responses, r => r.rowId === criteria.id);
        if (!response || !response.columnId) return acc;
        const descriptor = _.find(
          _.get(criteria, "descriptors", []),
          d => d.id === response.columnId
        );
        return acc + Number(_.get(descriptor, "score", 0) || 0);
      },
      0
    );
  }, [toolResponsesDataLocal, assessmentId, assessmentToolId, assessmentTool]);

  const maxRubricScore = _.get(
    assessmentTool,
    "associateScoreTool.scoreToolData.maxScore",
    0
  );
  const totalScore = rubricScore + (isBonusAwarded ? 1 : 0);
  const totalMaxScore = Number(maxRubricScore || 0) + 1;

  const tutorGradeAndSubject = useMemo(() => {
    const grade =
      _.get(entry, "tutor.metaData.tutorContext.grades", "") ||
      _.get(entry, "tutor.grades[0].name", "");
    const subjectField = _.find(_.get(entry, "tutor.fields", []), {
      uid: "subjects",
    });
    const subject = _.get(
      subjectField,
      "resolvedMinimalTree.subjects[0].name",
      ""
    );
    return _.compact([grade, subject]).join(" · ");
  }, [entry]);

  const handleOpen = useCallback(async () => {
    setActiveTab("submission");
    // If the target tutor doesn't have a SCORE_BASED_RUBRIC yet, clone it
    // from the source template tutor so the rubric is available when
    // ToolResponse renders. The drawer body shows a loader meanwhile.
    if (entry && !rubricTool) {
      setIsOpen(true);
      setIsCreatingRubric(true);
      try {
        await dispatch(
          createAssessmentTool({
            parentId: assessmentId,
            parentType: "ASSESSMENT",
            assessmentToolType: "SCORE_BASED_RUBRIC",
            label: "",
            copyAssessmentToolId: RUBRIC_SOURCE_ASSESSMENT_ID,
            isGlobal: false,
          })
        );
        await refetch();
      } finally {
        setIsCreatingRubric(false);
      }
      return;
    }
    // Cell clicks and bonus toggles persist via mutations that don't write
    // back into Assessment.toolResponses / ibPYPElementRatings (no
    // ASSESSMENT branch in the cache helpers), so cached query results go
    // stale between drawer opens. Refetch before flipping isOpen so the
    // seed effect runs against fresh data.
    await refetch();
    setIsOpen(true);
  }, [entry, rubricTool, assessmentId, refetch, dispatch]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setToolResponsesDataLocal({});
    setIBPYPElementRatingsLocal({});
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = e => {
      if (e.key === "Escape") handleClose();
    };
    const handleMouseDown = e => {
      if (
        drawerContentRef.current &&
        !drawerContentRef.current.contains(e.target)
      ) {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [isOpen, handleClose]);

  const handleSwitchToPlayTab = useCallback(() => setActiveTab("play"), []);

  // Bonus toggle's only side-effect is to upsert the SCORE element rating
  // for this assessment. The rubric descriptor selections save in real time
  // through ToolResponse's default flow; the bonus is the staff override
  // signal layered on top.
  const handleBonusChange = useCallback(
    async next => {
      setIsBonusAwarded(next);
      const userId = _.get(getUserInfo(), "id");
      if (!assessmentId || !userId) return;
      try {
        await dispatch(
          createIbPypElementRatingsWithoutValue({
            elementRatings: [
              {
                // TODO: replace with the real reviewer staff id once the BE
                // upsert mutation lands.
                studentId: "380460899988683963",
                parentId: assessmentId,
                parentType: "ASSESSMENT",
                ibPYPElementType: "SCORE",
                value: next ? "1" : "0",
                metadata: { isBonus: next },
                createdBy: userId,
                academicCriteriaValueId: "-9999",
                academicCriteriaSetId: "-9999",
              },
            ],
          })
        );
        // The thunk doesn't write back into the Assessment.ibPYPElementRatings
        // cache, so refetch to keep the seed effect in sync with what the BE
        // persisted (otherwise reopening the drawer would not reflect the new
        // SCORE rating).
        await refetch();
      } catch (err) {
        setIsBonusAwarded(!next);
      }
    },
    [assessmentId, dispatch, refetch]
  );

  const handleSubmit = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleResetChat = useCallback(async () => {
    if (!previewConversationId) return;
    // Mirror the wrapper's `resetChat` flow (AITutorChatWrapper.js:1487):
    // 1. archive existing messages
    // 2. regenerate the opening message
    // 3. remount the wrapper so it re-renders with the new conversation
    setIsResettingChat(true);
    try {
      await dispatch(
        resetAITutorConversation({ input: { id: previewConversationId } })
      );
      await dispatch(
        createOpeningMessage({
          leId: assessmentId,
          conversationId: previewConversationId,
        })
      );
      setChatSession(s => s + 1);
    } finally {
      setIsResettingChat(false);
    }
  }, [previewConversationId, assessmentId, dispatch]);

  if (!isAITutor) return null;

  return (
    <>
      {entry ? (
        <Button
          dsVersion="2.0"
          size="medium"
          variant="progressive"
          type="fill"
          icon={<ClipboardPencilOutlined variant="on" />}
          onClick={handleOpen}
          testId="aiTutorAwards-reviewer-open"
        >
          Review and evaluate
        </Button>
      ) : null}

      {entry && isOpen ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1100,
            background: "rgba(20, 15, 10, 0.42)",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <div
            ref={drawerContentRef}
            role="dialog"
            aria-modal="true"
            aria-label="Submission details"
            style={{
              width: activeTab === "play" ? "100vw" : 920,
              maxWidth: "100vw",
              height: "100vh",
              background: "var(--surface-primary-enabled)",
              boxShadow: "-8px 0 32px rgba(20, 15, 10, 0.15)",
              display: "flex",
              flexDirection: "column",
              transition: "width 0.25s ease",
            }}
          >
            <div className={classes.drawerHeaderRow}>
              <div className={classes.drawerHeaderTitle}>
                <AssessmentIcon
                  assessmentType="ai_tutor"
                  src={_.get(entry, "tutor.image.value") || undefined}
                  size="medium"
                />
                <div className={classes.drawerHeaderTitleStack}>
                  <span
                    className={classNames(
                      classes.drawerHeaderTitleText,
                      "text-body"
                    )}
                  >
                    {_.get(entry, "tutor.title.value") || "Untitled tutor"}
                  </span>
                  {tutorGradeAndSubject ? (
                    <span
                      className={classNames(
                        classes.drawerHeaderSubtitle,
                        "text-label-s"
                      )}
                    >
                      {tutorGradeAndSubject}
                    </span>
                  ) : null}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {activeTab === "play" ? (
                  <IconButton
                    size="medium"
                    variant="neutral"
                    type="outlined"
                    icon={<RefreshArrowOutlined variant="subtle" />}
                    onClick={handleResetChat}
                    disabled={!previewConversationId}
                    aria-label="Reset chat"
                    testId="aiTutorAwards-reviewer-play-reset"
                  />
                ) : null}
                <IconButton
                  size="medium"
                  variant="neutral"
                  type="plain"
                  icon={<CloseOutlined />}
                  onClick={handleClose}
                  aria-label="Close"
                  testId="aiTutorAwards-reviewer-close"
                />
              </div>
            </div>

            <div className={classes.drawerTabs}>
              <button
                type="button"
                className={classNames(classes.drawerTab, "text-label-s", {
                  [classes.drawerTabActive]: activeTab === "submission",
                })}
                onClick={() => setActiveTab("submission")}
                data-test-id="aiTutorAwards-reviewer-tab-submission"
              >
                <ClipboardPencilOutlined size="xx-small" />
                Submission
              </button>
              <button
                type="button"
                className={classNames(classes.drawerTab, "text-label-s", {
                  [classes.drawerTabActive]: activeTab === "play",
                })}
                onClick={() => setActiveTab("play")}
              >
                <PlayCircleOutlined size="xx-small" />
                Play tutor
              </button>
              <button
                type="button"
                className={classNames(classes.drawerTab, "text-label-s", {
                  [classes.drawerTabActive]: activeTab === "evaluate",
                })}
                onClick={() => setActiveTab("evaluate")}
              >
                <QuickTaskOutlined size="xx-small" />
                Evaluate
              </button>
            </div>

            <div className={classes.drawerTabPanel}>
              {isCreatingRubric ? (
                <div className={classes.drawerBody}>
                  <Loading position="center" />
                </div>
              ) : activeTab === "submission" ? (
                <div className={classes.drawerBody}>
                  <Section label="Subject">
                    <span
                      className={classNames(
                        classes.sectionValueStrong,
                        "text-body"
                      )}
                    >
                      {categoryKey ? t(categoryKey) : "—"}
                    </span>
                  </Section>

                  <Section label="What made it special">
                    <span
                      className={classNames(
                        classes.sectionValueStrong,
                        "text-body"
                      )}
                    >
                      {specialityKey ? t(specialityKey) : "—"}
                    </span>
                  </Section>

                  <Section label="Submitter's story">
                    {entry.description ? (
                      <div
                        className={classNames(classes.storyCard, "text-body")}
                        // Story comes from the submitter's RTE on AITutorAwardsModal,
                        // which produces sanitized HTML.
                        dangerouslySetInnerHTML={{ __html: entry.description }}
                      />
                    ) : (
                      <span className={classes.empty}>No story added.</span>
                    )}
                  </Section>

                  <button
                    type="button"
                    className={classes.tryTutorCallout}
                    onClick={handleSwitchToPlayTab}
                    aria-label="Try the tutor before evaluating"
                  >
                    <PlayCircleOutlined variant="subtle" />
                    <div className={classes.tryTutorCalloutBody}>
                      <span
                        className={classNames(
                          classes.tryTutorCalloutTitle,
                          "text-body"
                        )}
                      >
                        Try the tutor before evaluating
                      </span>
                      <span
                        className={classNames(
                          classes.tryTutorCalloutHint,
                          "text-label-s"
                        )}
                      >
                        Switch to the &quot;Play tutor&quot; tab to interact
                        with it as a student would.
                      </span>
                    </div>
                  </button>
                </div>
              ) : activeTab === "play" ? (
                <div className={classes.playModeContainer}>
                  <div className={classes.playModeBody}>
                    <div className={classes.playModeChat}>
                      <AITutorChatWrapper
                        key={chatSession}
                        leId={assessmentId}
                        entityId={assessmentId}
                        source="STUDENT_PREVIEW"
                        entitySubType="PREVIEW"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className={classes.drawerBody}>
                  <Section
                    label="Evaluation rubric"
                    headerRight={
                      <span
                        className={classNames(
                          classes.totalChip,
                          "text-label-s"
                        )}
                      >
                        {totalScore}
                        <span className={classes.totalChipMax}>
                          /{totalMaxScore}
                        </span>
                      </span>
                    }
                  >
                    {assessmentTool ? (
                      <ToolResponse
                        toolType="SCORE_BASED_RUBRIC"
                        assessmentTool={assessmentTool}
                        assessmentToolId={assessmentToolId}
                        parentId={assessmentId}
                        parentType="ASSESSMENT"
                        // TODO: replace with the real reviewer staff id once
                        // the BE upsert mutation lands. Required so the
                        // `handleIBPYPElementRatingsForStandardRubric` thunk
                        // passes its `studentId` guard and dispatches the
                        // SCORE rating mutation alongside the cell save.
                        studentId="380460899988683963"
                        isEvaluationEnable={true}
                        isEvaluation={true}
                        showCommentHeading={false}
                        shouldTakeFullWidth={true}
                        containerStyle={styles.toolResponseContainerStyle}
                        showDescriptionToggle={false}
                        showOverrideScore={false}
                        evaluationStatusExtraParams={
                          styles.evaluationStatusExtraParams
                        }
                        toolResponsesDataLocal={toolResponsesDataLocal}
                        setToolResponsesDataLocal={setToolResponsesDataLocal}
                        ibPYPElementRatingsLocal={ibPYPElementRatingsLocal}
                        setIBPYPElementRatingsLocal={
                          setIBPYPElementRatingsLocal
                        }
                      />
                    ) : (
                      <span className={classes.empty}>
                        Rubric not attached to this assessment yet.
                      </span>
                    )}
                    <BonusPointRow
                      checked={isBonusAwarded}
                      onChange={handleBonusChange}
                    />
                  </Section>

                  <Section label="Reviewer remarks">
                    <div className={classes.editor}>
                      <RichTextEditor
                        value={remarks}
                        updateInputField={handleRemarksChange}
                        placeholder="Share what stood out, what could improve, and any moments worth flagging."
                        height="220px"
                        pluginsConfig={RTE_PLUGINS_CONFIG}
                        shouldCallOnBlur={false}
                      />
                    </div>
                  </Section>
                </div>
              )}
            </div>

            <div className={classes.drawerFooter}>
              <Button
                dsVersion="2.0"
                size="medium"
                variant="neutral"
                type="outlined"
                onClick={handleClose}
                testId="aiTutorAwards-reviewer-cancel"
              >
                Cancel
              </Button>
              <Button
                dsVersion="2.0"
                size="medium"
                variant="progressive"
                type="fill"
                onClick={handleSubmit}
                testId="aiTutorAwards-reviewer-submit"
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {isResettingChat ? <FullScreenLoader /> : null}
    </>
  );
};

export default AITutorAwardsReviewer;
